import {
  Button,
  Dialog,
  DialogTrigger,
  Form,
  Heading,
  Modal,
  ModalOverlay,
  Text,
} from 'react-aria-components'
import {toast} from 'sonner'
import OTPInput from 'src/components/OTPInput/OTPInput'
import {deleteAccount, triggerAccountDeletion} from 'src/services/authenticationService'

import PrimaryButton from '@/components/PrimaryButton/PrimaryButton'
import toastErrorMessage from '@/utils/errors/toastErrorMessage'
import style from '@/views/Account/Profile/Profile.module.scss'

export default memo(function RequestDeletion() {
  const router = useRouter()
  const [isRequestingDeletion, setIsRequestingDeletion] = useState(false)
  const [isRequestedDeletion, setIsRequestedDeletion] = useState(false)
  const [isDeletingAccount, setIsDeletingAccount] = useState(false)
  const [otp, setOtp] = useState('')
  const latestOtp = useLatest(otp)

  const handleOtpChange = (value: string) => {
    setOtp(value)
  }

  const isOtpCompleted = otp.length === 6

  const onRequestDeletion = () => {
    setIsRequestingDeletion(true)
    void triggerAccountDeletion({})
      .then(result => {
        if (result) {
          if (typeof result === 'string') {
            toastErrorMessage(result)
          } else {
            setIsRequestedDeletion(true)
          }
        } else {
          toastErrorMessage()
        }
      })
      .finally(() => {
        setIsRequestingDeletion(false)
      })
  }

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>, onDone?: () => void) => {
    e.preventDefault()
    if (!isOtpCompleted) return
    console.error('submit')
    setIsDeletingAccount(true)
    void deleteAccount({code: latestOtp.current})
      .then(result => {
        if (result) {
          if (typeof result === 'string') {
            toastErrorMessage(result)
          } else {
            toast.success('Account deleted successfully')
            onDone?.()
          }
        } else {
          toastErrorMessage()
        }
      })
      .finally(() => {
        setIsDeletingAccount(false)
        void router.invalidate()
      })
  }

  return (
    <>
      <h2>Account deletion</h2>
      <div className={style.RequestDeletionArea}>
        <DialogTrigger>
          <PrimaryButton>Request account deletion</PrimaryButton>
          <ModalOverlay
            // TODO: add animation
            className={({isEntering, isExiting}) =>
              clsx(style.Overlay, isEntering && style.IsEntering, isExiting && style.IsExiting)
            }
            isDismissable
          >
            <Modal className={style.Modal}>
              <Dialog className={style.Dialog}>
                {({close}) => {
                  if (isRequestedDeletion) {
                    return (
                      <>
                        <Heading className={style.Heading} slot='title'>
                          Account deletion
                        </Heading>
                        <Form
                          onSubmit={e => {
                            handleSubmit(e, close)
                          }}
                        >
                          <div className={style.Description}>
                            <Text>
                              To delete your account, please enter the OTP code that has been sent
                              to your email. This action is irreversible, please proceed with
                              caution.
                            </Text>
                            <div className={style.OTPInputContainer}>
                              <OTPInput
                                aria-label='OTP code'
                                value={otp}
                                onChange={handleOtpChange}
                              />
                              <div>
                                If you didn't receive the OTP code, you can{' '}
                                <PrimaryButton
                                  size='md'
                                  isPending={isRequestingDeletion}
                                  className={style.ResendBtn}
                                  onPress={onRequestDeletion}
                                  showPendingIndicator={false}
                                >
                                  {isRequestingDeletion ? 'Resending OTP...' : 'Resend OTP'}
                                </PrimaryButton>
                              </div>
                            </div>
                          </div>
                          <div className={style.Action}>
                            <Button className={style.Cancel} onPress={close}>
                              Cancel
                            </Button>
                            <PrimaryButton
                              type='submit'
                              isDisabled={!isOtpCompleted}
                              isPending={isDeletingAccount}
                            >
                              Delete account
                            </PrimaryButton>
                          </div>
                        </Form>
                      </>
                    )
                  }

                  return (
                    <>
                      <Heading className={style.Heading} slot='title'>
                        Request account deletion
                      </Heading>
                      <div className={style.Description}>
                        <Text>Are you sure you want to request account deletion?</Text>
                      </div>
                      <div className={style.Action}>
                        <Button className={style.Cancel} onPress={close}>
                          Cancel
                        </Button>
                        <PrimaryButton onPress={onRequestDeletion} isPending={isRequestingDeletion}>
                          Request deletion
                        </PrimaryButton>
                      </div>
                    </>
                  )
                }}
              </Dialog>
            </Modal>
          </ModalOverlay>
        </DialogTrigger>
      </div>
    </>
  )
})
